import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type PriceType } from '@/api/refs';
import Drawer from '@/components/Drawer';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Довідник «Тип цін» (tenant-scoped, alembic 0260, 2026-06-08).
 *
 * Вид типу:
 * - Базовий — ціна вводиться вручну у закладці «Ціни» картки номенклатури.
 * - Динамічний — ціна обчислюється = ціна базового типу × (1 + наценка%/100).
 *   Обирається базовий тип-джерело + відсоток наценки.
 *
 * Дефолтні 3 базові типи (Закупівельна/Роздрібна/Оптова) — не видаляються.
 */
const SEED_CODES = new Set(['PURCHASE', 'RETAIL', 'WHOLESALE']);

interface FormState {
  id: string | null;
  name: string;
  currency_id: string;
  price_includes_vat: boolean;
  is_purchase: boolean;
  purchase_calc_method: 'LAST' | 'AVERAGE' | 'MAX';
  fx_adjust_enabled: boolean;
  fx_currency_id: string;
  fx_threshold_percent: string;
  fx_direction: 'UP' | 'DOWN' | 'BOTH';
  kind: 'BASE' | 'DYNAMIC';
  base_price_type_id: string;
  markup_percent: string;
}

const EMPTY = (uahId: string): FormState => ({
  id: null, name: '', currency_id: uahId, price_includes_vat: true,
  is_purchase: false, purchase_calc_method: 'LAST',
  fx_adjust_enabled: false, fx_currency_id: '', fx_threshold_percent: '', fx_direction: 'BOTH',
  kind: 'BASE', base_price_type_id: '', markup_percent: '',
});

export default function PriceTypesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);

  const list = useQuery({ queryKey: ['price-types'], queryFn: refs.priceTypes.list });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });

  const uahId = (currenciesQ.data ?? []).find((c) => c.code === 'UAH')?.id ?? '';
  const currCode = (id: string) => (currenciesQ.data ?? []).find((c) => c.id === id)?.code ?? '';
  const invalidate = () => qc.invalidateQueries({ queryKey: ['price-types'] });

  const rows = list.data ?? [];
  // Базові типи — джерела для динамічних (anti-chain: джерело має бути BASE).
  const baseTypes = useMemo(() => rows.filter((t) => t.kind === 'BASE'), [rows]);

  const save = useMutation({
    mutationFn: () => {
      const f = form!;
      const isDyn = f.kind === 'DYNAMIC';
      // ч.928: валютне коригування — лише для базового закупівельного типу.
      const fxOn = f.fx_adjust_enabled && f.is_purchase && !isDyn;
      const payload = {
        name: f.name.trim(),
        currency_id: f.currency_id,
        price_includes_vat: f.price_includes_vat,
        is_purchase: f.is_purchase,
        purchase_calc_method: f.purchase_calc_method,
        fx_adjust_enabled: fxOn,
        fx_currency_id: fxOn ? f.fx_currency_id : null,
        fx_threshold_percent: fxOn ? f.fx_threshold_percent : null,
        fx_direction: f.fx_direction,
        kind: f.kind,
        base_price_type_id: isDyn ? f.base_price_type_id : null,
        markup_percent: isDyn ? f.markup_percent : null,
      };
      if (f.id) return refs.priceTypes.update(f.id, payload);
      return refs.priceTypes.create(payload);
    },
    onSuccess: () => { invalidate(); setForm(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.priceTypes.remove(id),
    onSuccess: invalidate,
  });

  const startEdit = (t: PriceType) => setForm({
    id: t.id, name: t.name, currency_id: t.currency_id,
    price_includes_vat: t.price_includes_vat, is_purchase: t.is_purchase,
    purchase_calc_method: t.purchase_calc_method ?? 'LAST',
    fx_adjust_enabled: !!t.fx_adjust_enabled, fx_currency_id: t.fx_currency_id ?? '',
    fx_threshold_percent: t.fx_threshold_percent != null ? String(Number(t.fx_threshold_percent)) : '',
    fx_direction: t.fx_direction ?? 'BOTH',
    kind: t.kind, base_price_type_id: t.base_price_type_id ?? '',
    markup_percent: t.markup_percent ?? '',
  });

  const isDyn = form?.kind === 'DYNAMIC';
  // ч.928: валютне коригування — іноземні валюти курсу НБУ (долар / євро).
  const fxCurrencies = (currenciesQ.data ?? []).filter((c) => c.code === 'USD' || c.code === 'EUR');
  const fxActive = !!form?.fx_adjust_enabled && !!form?.is_purchase && !isDyn;
  const canSave = !!form?.name.trim() && !!form?.currency_id
    && (!isDyn || (!!form?.base_price_type_id && form?.markup_percent !== ''))
    && (!fxActive || (!!form?.fx_currency_id && Number(form?.fx_threshold_percent) > 0))
    && !save.isPending;

  const cols = useMemo<ColumnDef<PriceType>[]>(() => [
    {
      key: 'name', header: 'Назва', width: 250,
      sortValue: (t) => t.name.toLowerCase(),
      cell: (t) => {
        const seed = t.code != null && SEED_CODES.has(t.code);
        return <>{seed && <span title="Дефолтний тип">🔒 </span>}{t.name}</>;
      },
    },
    {
      key: 'kind', header: 'Вид', width: 130, align: 'center',
      sortValue: (t) => (t.kind === 'DYNAMIC' ? 'Динамічний' : 'Базовий'),
      cell: (t) => (t.kind === 'DYNAMIC' ? 'Динамічний' : 'Базовий'),
    },
    {
      key: 'currency', header: 'Валюта', width: 100, align: 'center',
      sortValue: (t) => currCode(t.currency_id),
      cell: (t) => currCode(t.currency_id),
    },
    {
      key: 'vat', header: 'З ПДВ', width: 90, align: 'center',
      sortValue: (t) => (t.price_includes_vat ? 1 : 0),
      cell: (t) => (t.price_includes_vat ? '✓' : ''),
    },
    {
      key: 'markup', header: 'Наценка', width: 110, align: 'center',
      sortValue: (t) => (t.kind === 'DYNAMIC' && t.markup_percent != null ? Number(t.markup_percent) : -1),
      cell: (t) => (t.kind === 'DYNAMIC' && t.markup_percent != null
        ? `${Number(t.markup_percent)}%` : '—'),
    },
    {
      // ч.928: валютне коригування закупівельної ціни — валюта, поріг, напрям.
      key: 'fx', header: 'Вал. коригування', width: 150, align: 'center',
      sortValue: (t) => (t.fx_adjust_enabled ? currCode(t.fx_currency_id ?? '') : ''),
      cell: (t) => (t.fx_adjust_enabled
        ? `${currCode(t.fx_currency_id ?? '')} ${Number(t.fx_threshold_percent)}% ${
          t.fx_direction === 'UP' ? '↑' : t.fx_direction === 'DOWN' ? '↓' : '↑↓'}`
        : '—'),
    },
    {
      key: 'actions', header: '', width: 110, align: 'right',
      cell: (t) => {
        const seed = t.code != null && SEED_CODES.has(t.code);
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <button title="Редагувати" onClick={() => startEdit(t)}>✎</button>
            {!seed && (
              <button className="danger" title="Видалити" style={{ marginLeft: '0.25rem' }}
                onClick={() => confirm(`Видалити тип «${t.name}»?`).then((ok) => { if (ok) remove.mutate(t.id); })}>✕</button>
            )}
          </span>
        );
      },
    },
  ], [currCode, confirm, remove]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Типи цін</h2>
        <button className="primary" onClick={() => setForm(EMPTY(uahId))}>+ Новий тип</button>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 720 }}>
        Тип ціни визначає, яку ціну застосовувати (наприклад, «Роздрібна»,
        «Оптова»). <strong>Базовий</strong> тип — ціна вводиться вручну.
        <strong> Динамічний</strong> — ціна рахується автоматично від базового
        плюс наценка (наприклад, «Роздрібна = Закупівельна + 30%»). Дефолтні
        типи позначені 🔒 — їх не можна видалити.
      </p>

      <DataTable<PriceType>
        rows={rows}
        columns={cols}
        rowKey={(t) => t.id}
        loading={list.isLoading}
        emptyText="Немає типів цін."
        initialSortKey="name"
      />

      {form && (
        <Drawer
          open={!!form}
          title={form.id ? 'Редагувати тип ціни' : 'Новий тип ціни'}
          onClose={() => setForm(null)}
          footer={
            <>
              <button onClick={() => setForm(null)}>Скасувати</button>
              <button className="primary" disabled={!canSave} onClick={() => save.mutate()}>
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label>Назва
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            </label>
            <label>Валюта
              <select value={form.currency_id} onChange={(e) => setForm({ ...form, currency_id: e.target.value })}>
                {(currenciesQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.price_includes_vat}
                onChange={(e) => setForm({ ...form, price_includes_vat: e.target.checked })} />
              Ціна включає ПДВ
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={form.is_purchase}
                onChange={(e) => setForm({ ...form, is_purchase: e.target.checked })} />
              Закупівельний тип
            </label>
            {/* ч.921 (рішення власника): закупівельна ціна рахується з проведених
                надходжень — спосіб обирається тут (дефолт — остання закупівля). */}
            {form.is_purchase && (
              <label>Як рахувати закупівельну ціну
                <select value={form.purchase_calc_method}
                  onChange={(e) => setForm({ ...form, purchase_calc_method: e.target.value as 'LAST' | 'AVERAGE' | 'MAX' })}>
                  <option value="LAST">за останньою закупівлею</option>
                  <option value="AVERAGE">середня (зважена за кількістю)</option>
                  <option value="MAX">максимальна</option>
                </select>
                <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
                  Рахується з проведених надходжень товарів. Середня і максимальна — за
                  закупівлями останніх 12 місяців; якщо їх не було — береться остання
                  закупівля, а якщо закупівель немає взагалі — ціна з прайсу цього типу.
                </small>
              </label>
            )}
            {/* ч.928 (рішення власника): валютне коригування закупівельної ціни. */}
            {form.is_purchase && !isDyn && (
              <>
                <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input type="checkbox" checked={form.fx_adjust_enabled}
                    onChange={(e) => setForm({
                      ...form, fx_adjust_enabled: e.target.checked,
                      fx_currency_id: form.fx_currency_id
                        || fxCurrencies.find((c) => c.code === 'EUR')?.id || fxCurrencies[0]?.id || '',
                      fx_threshold_percent: form.fx_threshold_percent || '1',
                    })} />
                  Валютне коригування
                </label>
                {form.fx_adjust_enabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem',
                    paddingLeft: '1.4rem' }}>
                    <div style={{ display: 'flex', gap: '0.6rem' }}>
                      <label style={{ flex: 1 }}>Валюта (курс НБУ)
                        <select value={form.fx_currency_id}
                          onChange={(e) => setForm({ ...form, fx_currency_id: e.target.value })}>
                          {fxCurrencies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                        </select>
                      </label>
                      <label style={{ flex: 1 }}>Зміна курсу, %
                        <input type="number" step="0.01" min="0.01" value={form.fx_threshold_percent}
                          onChange={(e) => setForm({ ...form, fx_threshold_percent: e.target.value })} />
                      </label>
                    </div>
                    <label>Напрям
                      <select value={form.fx_direction}
                        onChange={(e) => setForm({ ...form, fx_direction: e.target.value as 'UP' | 'DOWN' | 'BOTH' })}>
                        <option value="UP">лише зростання курсу</option>
                        <option value="DOWN">лише падіння курсу</option>
                        <option value="BOTH">зростання і падіння</option>
                      </select>
                    </label>
                    <small style={{ color: 'var(--muted)' }}>
                      Ціна закупівлі вірна на дату закупівлі. Далі щодня дивимось курс НБУ:
                      коли накопичена зміна курсу (від дати закупівлі, а потім — від
                      останнього перегляду) сягає вказаного відсотка у вибраний бік,
                      закупівельна ціна перераховується на цю зміну. Обмеження «за тиждень»
                      немає — зміна накопичується скільки завгодно довго.
                    </small>
                  </div>
                )}
              </>
            )}
            <label>Вид типу
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as 'BASE' | 'DYNAMIC' })}>
                <option value="BASE">Базовий (ціна вручну)</option>
                <option value="DYNAMIC">Динамічний (від базового + наценка)</option>
              </select>
            </label>
            {isDyn && (
              <>
                <label>Базовий тип-джерело
                  <select value={form.base_price_type_id}
                    onChange={(e) => setForm({ ...form, base_price_type_id: e.target.value })}>
                    <option value="">— оберіть базовий тип —</option>
                    {baseTypes
                      .filter((b) => b.id !== form.id && b.currency_id === form.currency_id)
                      .map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </label>
                <label>Наценка, %
                  <input type="number" step="0.01" value={form.markup_percent}
                    onChange={(e) => setForm({ ...form, markup_percent: e.target.value })} />
                </label>
              </>
            )}
          </div>
        </Drawer>
      )}
    </div>
  );
}
